import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { images } from "../data/content";

export default function PaymentResult({ cancelled = false }) {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [tries, setTries] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState("error");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") setState("paid");
      else if (["failed", "expired"].includes(data.payment_status)) setState("error");
      else setTries((t) => t + 1);
    } catch {
      setTries((t) => t + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) return;
    if (state !== "checking") return;
    if (tries > 8) {
      setState("pending");
      return;
    }
    const id = setTimeout(poll, tries === 0 ? 400 : 2000);
    return () => clearTimeout(id);
  }, [poll, tries, state, cancelled]);

  const config = {
    checking: { icon: <Loader2 size={54} className="animate-spin text-gold-deep" />, title: "Confirming your payment…", body: "Please wait a moment while we confirm your dues payment with Stripe." },
    paid: { icon: <CheckCircle2 size={54} className="text-emerald" />, title: "Payment received — thank you.", body: "Your annual dues are now marked as paid. A record has been added to your payment history." },
    pending: { icon: <Loader2 size={54} className="text-gold-deep" />, title: "Payment is processing.", body: "This is taking longer than usual. Your dues status will update automatically once confirmed." },
    cancelled: { icon: <XCircle size={54} className="text-garnet" />, title: "Payment cancelled.", body: "No charge was made. You can return to the portal and try again whenever you're ready." },
    error: { icon: <XCircle size={54} className="text-garnet" />, title: "We couldn't confirm this payment.", body: "If you were charged, it will reconcile shortly. Otherwise please try again from the Dues page." },
  }[state];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-sapphire-deep px-6">
      <img src={images.templeInterior} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-sapphire-deep/70" />
      <div className="relative z-10 w-full max-w-lg rounded-3xl border border-gold/25 bg-ivory p-10 text-center shadow-heritage">
        <img src={images.crestSeal} alt="Lodge crest" className="mx-auto h-16 w-16" />
        <div className="mt-6 flex justify-center">{config.icon}</div>
        <h1 className="mt-5 font-display text-3xl font-semibold text-sapphire-deep">{config.title}</h1>
        <p className="mt-3 font-sans text-ink/70">{config.body}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/portal/dues" className="btn-sapphire">View Dues <ArrowRight size={16} /></Link>
          <Link to="/portal" className="btn-dark">Back to Portal</Link>
        </div>
      </div>
    </div>
  );
}
